﻿import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import { CatalogTypes } from "@/components/Maintenance/MaintenanceTypes";
import axios from "@/features/axiosHelper";
import type { UUIDTypes } from "uuid";
import type { RootState } from "@/typedDispatch"
import type { IConnectionInfo, IThunkConfig } from "../sharedTypes";
interface IFilters {
  type: string[];
}

export interface IConnectionCustomFieldAndValue {
  ConnectionId: number;
  ConnectionName: string;
  CatalogCustomFieldId: UUIDTypes
  FieldCode: string;
  FieldValue: string; 
}
interface IFetchCrossReferenceSummaryResult {
  CrossReferenceMappingId: UUIDTypes;
  ConnectionPairId: number;
  CrossReferenceType: number;
  Priority: string; 
  ConnectionName: string; 
  ConnectionName2: string; 
  ConnectionCode: string; 
  ConnectionCode2: string; 
  CustomerRelatedAccount: string; 
  CustomerItemCode: string; 
  ErpConnectionName: string; 
  ErpItemCode: string; 
  CustomerItemUOM: string; 
  CustomerUOMFactor: string; 
  ErpBaseUOM: string; 
  ErpUOMFactor: string; 
  ErpUOMCode: string; 
  CustomerBaseUOM: string; 
  ErpItemDescription: string; 
  ErpItemWeight: string; 
  CustomerItemDescription: string; 
  CustomerItemWeight: string; 
  CustomerConnectionName: string; 
  CustomerWarehouseCode: string; 
  ErpWarehouseCode: string; 
  CustomerWarehouseName: string; 
  CustomerWarehouseAddress1: string; 
  CustomerWarehouseAddress2: string; 
  CustomerWarehouseCity: string; 
  CustomerWarehouseState: string; 
  CustomerWarehousePostalCode: string; 
  CustomerWarehouseCountry: string; 
  CustomerWarehouseContact: string; 
  CustomerWarehousePhone: string; 
  CustomerWarehouseFax: string; 
  CustomerWarehouseEmail: string; 
  ErpWarehouseName: string; 
  ErpWarehouseAddress1: string; 
  ErpWarehouseAddress2: string; 
  ErpWarehouseCity: string; 
  ErpWarehouseState: string; 
  ErpWarehousePostalCode: string; 
  ErpWarehouseCountry: string; 
  ErpWarehouseContact: string; 
  ErpWarehousePhone: string; 
  ErpWarehouseFax: string; 
  ErpWarehouseEmail: string; 
  ErpCustomerName: string; 
  CustomerCustomerName: string; 
  ErpBillToName: string; 
  CustomerBillToName: string; 
  ErpBillToAddress1: string; 
  CustomerBillToAddress1: string; 
  ErpBillToAddress2: string; 
  CustomerBillToAddress2: string; 
  ErpBillToAddress3: string; 
  CustomerBillToAddress3: string; 
  ErpBillToAddress4: string; 
  CustomerBillToAddress4: string; 
  ErpBillToCity: string; 
  CustomerBillToCity: string; 
  ErpBillToState: string; 
  CustomerBillToState: string; 
  ErpBillToPostalCode: string; 
  CustomerBillToPostalCode: string; 
  ErpBillToCountry: string; 
  CustomerBillToCountry: string; 
  ErpBillToPhone: string; 
  CustomerBillToPhone: string; 
  ErpBillToFax: string; 
  CustomerBillToFax: string; 
  ErpBillToEmail: string; 
  CustomerBillToEmail: string; 
  ErpBillToWebsite: string; 
  CustomerBillToWebsite: string; 
  CustomerShipToCode: string; 
  ErpShipToCode: string; 
  ErpShipToName: string; 
  CustomerShipToName: string; 
  ErpShipToAddress1: string; 
  CustomerShipToAddress1: string; 
  ErpShipToAddress2: string; 
  CustomerShipToAddress2: string; 
  ErpShipToAddress3: string; 
  CustomerShipToAddress3: string; 
  ErpShipToAddress4: string; 
  CustomerShipToAddress4: string; 
  ErpShipToCity: string; 
  CustomerShipToCity: string; 
  ErpShipToState: string; 
  CustomerShipToState: string; 
  ErpShipToPostalCode: string; 
  CustomerShipToPostalCode: string; 
  ErpShipToCountry: string; 
  CustomerShipToCountry: string; 
  ErpShipToPhone: string; 
  CustomerShipToPhone: string; 
  ErpShipToFax: string; 
  CustomerShipToFax: string; 
  ErpShipToEmail: string; 
  CustomerShipToEmail: string; 
  ServiceLevel: string; 
  Routing: string; 
  TransportationMethodType: string; 
  ErpCustomerCode: string; 
  CustomerCarrierCode: string; 
  ErpCarrierCode: string; 
  CustomerCarrierName: string; 
  CustomerScac: string; 
  CustomerRouting: string; 
  CustomerServiceLevel: string; 
  CustomerTransportationMethodType: string; 
  CustomerBillingAccountNumber: string; 
  ErpCarrierName: string; 
  ErpScac: string; 
  ErpRouting: string; 
  ErpServiceLevel: string; 
  ErpTransportationMethodType: string; 
  ErpBillingAccountNumber: string; 
  Qualifiers: string; 
  CustomerDiscountChargeCode: string; 
  CustomerDiscountChargeIndicator: string; 
  CustomerDiscountChargeHandlingCode: string; 
  CustomerDiscountChargeType: string; 
  ErpDiscountChargeCode: string; 
  ErpDiscountChargeIndicator: string; 
  ErpDiscountChargeHandlingCode: string; 
  ErpDiscountChargeType: string; 
  Connection1Name: string; 
  Connection2Name: string; 
  CustomerTaxGroupCode: string; 
  ErpTaxGroupCode: string; 
  CustomFields: IConnectionCustomFieldAndValue[];
}

interface IFetchCrossReferenceSummaryAction {
  connectionPairId: number;
  searchText: string;
  crossReferenceType?: number;
}
interface ICrossReferenceSummaryState {
  records: IFetchCrossReferenceSummaryResult[];
  recordCache: {
    [key: number]: IFetchCrossReferenceSummaryResult[] | null
  };
  recordsLoaded: boolean;
  loadingError: string | null;
  filters: IFilters;
  selectedTab: number;
  selectedConnectionPair: IConnectionInfo | null;
}
interface IConnectionQualifiersState {
  qualifiers: string[];
  qualifiersLoaded: boolean;
  loadingError: string | null;
}

interface ICatalogFieldsByArtifactType {
  [key: number]: {
    [key: string]: string;
  }
}
interface ICatalogDataFieldsState {
  dataFields: ICatalogFieldsByArtifactType;
  dataFieldsLoaded: boolean;
  loadingError: string | null;
}
interface ICustomFieldRecord {
  ConnectionCustomFieldId: UUIDTypes;
  CustomFieldCode: string;
  CustomFieldName: string;
  CustomFieldDescription: string;
}
interface ICustomFieldState {
  selectedConnectionCustomField: ICustomFieldRecord[];
  selectedConnectionId: number | null;
  allCustomFields: ICustomFieldRecord[];
  allConnections: number[];
  allCustomFieldsAndValues: ICustomFieldRecord[];
}

interface ICatalogSummaryState {
  records: (ICatalogSummaryResponse | null)[] | null;
  recordCount: number;
  recordsLoaded: boolean;
  loadingError: string | null;
  filters: IFilters;
  selectedTab: number;
  searchText: string;
  customFields: IFetchCustomFieldsResponse;
  customFieldsLoaded: boolean;
}

interface IMaintenanceSliceState {
  catalogSummary: ICatalogSummaryState;
  catalogDataFields: ICatalogDataFieldsState;
  crossReferenceSummary: ICrossReferenceSummaryState;
  connectionQualifiers: IConnectionQualifiersState;
  connectionPairDetails: IConnectionInfo[]; //TODO: could be the wrong type?
  connectionPairStatus: string;
  customFields: ICustomFieldState;
  error: string | null;
}

const maintenanceSlice = createSlice({
  name: "maintenance",
  initialState: {
    catalogDataFields: {
      dataFields: {},
      dataFieldsLoaded: false,
      loadingError: null,
    },
    catalogSummary: {
      records: [],
      recordCount: 0,
      recordsLoaded: false,
      loadingError: null,
      filters: {
        type: [],
      },
      selectedTab: CatalogTypes.Customer,
      searchText: "",
      customFields: [],
      customFieldsLoaded: false,
    },
    crossReferenceSummary: {
      records: [],
      recordCache: {},
      recordsLoaded: false,
      loadingError: null,
      filters: {
        type: [],
      },
      selectedTab: 0,
      selectedConnectionPair: null,
    },
    connectionQualifiers: {
      qualifiers: [],
      qualifiersLoaded: true,
      loadingError: null,
    },
    connectionPairDetails: [],
    connectionPairStatus: "idle",
    error: null,
    // custom fields
    customFields: {
      selectedConnectionCustomField: [],
      selectedConnectionId: null,
      allCustomFields: [],
      allConnections: [],
      allCustomFieldsAndValues: [],
    },
  } satisfies IMaintenanceSliceState as IMaintenanceSliceState,

  reducers: {
    setCatalogSummaryFilterTypes: (state, action) => {
      const { value } = action.payload;
      state.catalogSummary.filters.type = value;
    },
    setCrossReferenceSummaryFilterTypes: (state, action) => {
      const { value } = action.payload;
      state.crossReferenceSummary.filters.type = value;
    },
    setSelectedConnectionPair: (state, action) => {
      const { selectedConnectionPair } = action.payload;
      state.crossReferenceSummary.selectedConnectionPair =
        selectedConnectionPair;
    },
    setCatalogSummarySelectedTab: (state, action: PayloadAction<{
      selectedTab: number;
    }>) => {
      const { selectedTab } = action.payload;
      state.catalogSummary.selectedTab = selectedTab;
    },
    setCatalogSummarySearchText: (state, action) => {
      state.catalogSummary.searchText = action.payload;
    },
    setCrossReferenceSummarySelectedTab: (state, action) => {
      const { selectedTab } = action.payload;
      state.crossReferenceSummary.selectedTab = selectedTab;
    },
    setCatalogCustomFields: (state, action) => {
      const { customFields } = action.payload;
      state.catalogSummary.customFields = customFields;
    },
    deleteCrossReferenceSummaryRecords: (state, action) => {
      const idsToDelete = action.payload;
      state.crossReferenceSummary.records =
        state.crossReferenceSummary.records.filter(
          (record) => !idsToDelete.includes(record.CrossReferenceMappingId),
        );
    },
    clearAllRecordCaches: (state) => {
      state.crossReferenceSummary.recordCache = {};
      state.crossReferenceSummary.records = [];
    },
    clearTabCache: (state, action: PayloadAction<number>) => {
      const selectedTabIdToClear = action.payload;
      state.crossReferenceSummary.recordCache[selectedTabIdToClear] = null;
    },

    // custom fields
    setSelectedCustomField: (state, action) => {
      const { selectedCustomField } = action.payload;
      state.customFields.selectedConnectionCustomField = selectedCustomField;
    },

    setSelectedConnectionId: (state, action) => {
      const { selectedConnectionId } = action.payload;
      state.customFields.selectedConnectionId = selectedConnectionId;
    },

    setAllCustomFields: (state, action) => {
      const { allCustomFields } = action.payload;
      state.customFields.allCustomFields = allCustomFields;
    },

    setAllConnections: (state, action) => {
      const { allConnections } = action.payload;
      state.customFields.allConnections = allConnections;
    },

    setAllCustomFieldsAndValues: (state, action) => {
      const { allCustomFieldsAndValues } = action.payload;
      state.customFields.allCustomFieldsAndValues = allCustomFieldsAndValues;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCatalogFields.pending, (state) => {
        state.catalogDataFields.dataFieldsLoaded = false;
      })
      .addCase(fetchCatalogFields.rejected, (state, action) => {
        state.catalogDataFields.dataFieldsLoaded = true;
        state.catalogDataFields.loadingError = action.error.message ?? null;
      })
      .addCase(fetchCatalogFields.fulfilled, (state, action) => {
        state.catalogDataFields.dataFieldsLoaded = true;
        state.catalogDataFields.dataFields = action.payload;
      })
      .addCase(fetchItemQualifiers.pending, (state) => {
        state.connectionQualifiers.qualifiersLoaded = false;
      })
      .addCase(fetchItemQualifiers.rejected, (state, action) => {
        state.connectionQualifiers.qualifiersLoaded = true;
        state.connectionQualifiers.loadingError = action.error.message ?? null;
      })
      .addCase(fetchItemQualifiers.fulfilled, (state, action) => {
        state.connectionQualifiers.qualifiersLoaded = true;
        state.connectionQualifiers.qualifiers = action.payload;
      })
      .addCase(fetchCatalogSummaries.pending, (state, action) => {
        if (action.meta.arg.unloadRecords) {
          state.catalogSummary.recordsLoaded = false;
          state.catalogSummary.records = [];
          return;
        }

        // Only show loading screen if there are missing catalog items
        const pagination = action.meta.arg;
        const page = pagination.page ?? 0;
        for (
          let i = page * pagination.pageSize;
          i < (page + 1) * pagination.pageSize - 1;
          i++
        ) {
          if (state.catalogSummary.records && state.catalogSummary.records[i] === null) {
            state.catalogSummary.recordsLoaded = false;
          }
        }
      })
      .addCase(fetchCatalogSummaries.rejected, (state, action) => {
        state.catalogSummary.recordsLoaded = true;
        state.catalogSummary.loadingError = action.error.message ?? null;
      })
      .addCase(fetchCatalogSummaries.fulfilled, (state, action) => {
        const newCatalog = action.payload.unloadRecords
          ? []
          : state.catalogSummary.records;

        // Padding of not yet loaded records
        while (action.payload.startIndex && newCatalog && action.payload.startIndex > newCatalog.length) {
          newCatalog.push(null);
        }

        // Patch records from api request
        state.catalogSummary.records = newCatalog?.toSpliced(
          action.payload.startIndex ?? 0,
          action.payload.records.length,
          ...action.payload.records,
        ) ?? null;

        state.catalogSummary.recordCount = action.payload.totalCount;
        state.catalogSummary.recordsLoaded = true;
      })
      .addCase(fetchCrossReferenceSummaries.pending, (state, action) => {
        const tabId = action.meta.arg.crossReferenceType ?? -1;
        const isSearching = action.meta.arg.searchText;
        if (state.crossReferenceSummary.recordCache[tabId] && !isSearching) {
          state.crossReferenceSummary.records =
            state.crossReferenceSummary.recordCache[tabId];
          state.crossReferenceSummary.recordsLoaded = true;
        } else {
          state.crossReferenceSummary.recordsLoaded = false;
          state.crossReferenceSummary.records = [];
        }
      })
      .addCase(fetchCrossReferenceSummaries.rejected, (state, action) => {
        state.crossReferenceSummary.recordsLoaded = true;
        state.crossReferenceSummary.loadingError = action.error.message ?? null;
      })
      .addCase(fetchCrossReferenceSummaries.fulfilled, (state, action) => {
        const tabId = action.meta.arg.crossReferenceType ?? -1;
        const dataToSave = action.payload.map((record) => ({
          ...record,
          id: record.CrossReferenceMappingId,
        }));
        const isSearching = action.meta.arg.searchText;
        state.crossReferenceSummary.records = dataToSave;
        if (!isSearching) {
          state.crossReferenceSummary.recordCache[tabId] = dataToSave;
        }
        state.crossReferenceSummary.recordsLoaded = true;
      })
      .addCase(fetchConnectionPairs.pending, (state) => {
        state.connectionPairStatus = "loading";
      })
      .addCase(fetchConnectionPairs.rejected, (state, action) => {
        state.connectionPairStatus = "idle - failed";
        state.error = action.error.message ?? null;
      })
      .addCase(fetchConnectionPairs.fulfilled, (state, action) => {
        state.connectionPairStatus = "idle - loaded";
        state.connectionPairDetails = action.payload;
      })
      .addCase(fetchCatalogCustomFields.pending, (state) => {
        state.catalogSummary.customFieldsLoaded = false;
      })
      .addCase(fetchCatalogCustomFields.rejected, (state) => {
        state.catalogSummary.customFieldsLoaded = true;
      })
      .addCase(fetchCatalogCustomFields.fulfilled, (state, action) => {
        state.catalogSummary.customFieldsLoaded = true;
        state.catalogSummary.customFields = action.payload;
      });
  },
});

interface IFetchItemQualifiersAction {
  connectionId: number;
}
export const fetchItemQualifiers = createAsyncThunk<string[], IFetchItemQualifiersAction, IThunkConfig>(
  "maintenance/catalog-qualifiers",
  async ({ connectionId }, { rejectWithValue }) => {
    try {
      const response = await axios.get<string[]>(
        "/connectioncatalogartifacts/qualifiers",
        {
          params: { connectionId },
        },
      );

      return response.data;
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error("Error fetching catalogue identifiers:", error);
      return rejectWithValue(error.response.data);
    }
  },
);
export interface IFetchCatalogSummariesAction {
  page?: number;
  pageSize: number;
  search?: string;
  unloadRecords?: boolean;
}
interface IQualifierDto {
  Name: string;
  Value: string;
  Mandatory: boolean;
}
export interface ICatalogSummaryResponse {
  CatalogId: UUIDTypes;
  Qualifiers: IQualifierDto[];
  DataType: number;
  ConnectionId: number;
  Reference: string;
  CatalogData: string;
}
interface IFetchCatalogSummariesResponse {
  records: ICatalogSummaryResponse[];
  totalCount: number;
}

interface IFetchCatalogSummariesResult extends IFetchCatalogSummariesResponse {
  startIndex?: number;
  unloadRecords?: boolean;
}
export const fetchCatalogSummaries = createAsyncThunk<IFetchCatalogSummariesResult, IFetchCatalogSummariesAction, {state: RootState}>(
  "maintenance/catalog-summary",
  async (
    { page = 0, pageSize, search, unloadRecords = false },
    { getState },
  ) => {
    const state = getState() as RootState;
    const connectionId =
      state.tempInfo.integration.selectedConnectionInfo?.ConnectionId;
    const catalogArtifactType =
      state.tempInfo.maintenance?.catalogSummary?.selectedTab;

    if (!connectionId) {
      return {
        records: [],
        totalCount: 0,
      };
    }

    const response = await axios.get<IFetchCatalogSummariesResponse>("/connectioncatalogartifacts/summary", {
      params: {
        connectionId,
        artifactType: catalogArtifactType,
        offset: page * pageSize,
        count: pageSize,
        search: search,
      },
    });

    // Include start index & unload flag
    const result: IFetchCatalogSummariesResult = {
      startIndex: page * pageSize,
      unloadRecords,
      ...response.data
    }
    return result;
  },
);

interface IFetchCatalogFieldsAction {
  connectionId: number;
}

export const fetchCatalogFields = createAsyncThunk<ICatalogFieldsByArtifactType, IFetchCatalogFieldsAction, { state: RootState }>(
  "maintenance/catalog-fields",
  async({ connectionId }) => {
    if (!connectionId) {
      return {};
    }
    const response = await axios.get<ICatalogFieldsByArtifactType>("connectioncatalogartifacts/get-catalog-fields", {
      params: {
        connectionId,
      }
    });
    const result = response.data;
    return result;
  }
)

export const fetchCrossReferenceSummaries = createAsyncThunk<IFetchCrossReferenceSummaryResult[], IFetchCrossReferenceSummaryAction, IThunkConfig>(
  "maintenance/cross-reference-summary",
  async (
    { connectionPairId, searchText, crossReferenceType = -1 },
    { rejectWithValue, getState },
  ) => {
    try {
      const tabId = crossReferenceType ?? -1;
      const existingCache =
        getState().tempInfo.maintenance.crossReferenceSummary.recordCache[
          tabId
        ];
      //if data exists in cache and user isn't searching, skip backend call
      if (existingCache && !searchText) {
        return existingCache;
      }

      const response = await axios.get<IFetchCrossReferenceSummaryResult[]>("/crossreference/summary", {
        params: {
          connectionPair: connectionPairId,
          searchText: searchText,
          crossReferenceType: crossReferenceType,
        },
      });
      return response.data;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error("Error fetching cross-reference summaries:", error);
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchConnectionPairs = createAsyncThunk(
  "maintenance/fetchConnectionPairs",
  async () => {
    const response = await axios.get("/connectionpair", {
      params: {
        clearCache: false,
      },
    });
    return response.data;
  },
);

export interface ICustomFieldDto {
  FieldId: UUIDTypes;
  Code: string;
  Name: string;
  Description: string;
  CatalogType: number;
}

interface IFetchCustomFieldsResponse {
  [key: number]: ICustomFieldDto[];
}

export const fetchCatalogCustomFields = createAsyncThunk<IFetchCustomFieldsResponse, void>(
  "maintenance/fetchCatalogCustomFields",
  async () => {
    const response = await axios.get<IFetchCustomFieldsResponse>(
      "/connectioncatalogartifacts/custom-fields",
    );
    return response.data;
  },
);

export const selectCatalogSummaryFilterTypes = (state: RootState) =>
  state.tempInfo.maintenance?.catalogSummary?.filters?.type || [];
export const selectConnectionQualifiers = (state: RootState) =>
  state.tempInfo.maintenance?.connectionQualifiers.qualifiers || [];
export const selectConnectionQualifiersLoaded = (state: RootState) =>
  state.tempInfo.maintenance?.connectionQualifiers.qualifiersLoaded || false;
export const selectCatalogSummaryRecordsLoaded = (state: RootState) =>
  state.tempInfo.maintenance?.catalogSummary?.recordsLoaded || false;
export const selectCatalogSummaryRecords = (state: RootState) =>
  state.tempInfo.maintenance?.catalogSummary?.records || [];
export const selectCatalogSummaryRecordCount = (state: RootState) =>
  state.tempInfo.maintenance?.catalogSummary?.recordCount || 0;
export const selectCatalogSummarySelectedTab = (state: RootState) =>
  state.tempInfo.maintenance?.catalogSummary?.selectedTab;
export const selectCatalogSummarySearchText = (state: RootState) =>
  state.tempInfo.maintenance?.catalogSummary?.searchText || "";
export const selectCrossReferenceSummaryFilterTypes = (state: RootState) =>
  state.tempInfo.maintenance?.crossReferenceSummary?.filters?.type || [];
export const selectCrossReferenceSummaryRecordsLoaded = (state: RootState) =>
  state.tempInfo.maintenance?.crossReferenceSummary?.recordsLoaded || false;
export const selectCrossReferenceSummaryRecords = (state: RootState) =>
  state.tempInfo.maintenance?.crossReferenceSummary?.records || [];
export const selectCrossReferenceSummarySelectedTab = (state: RootState) =>
  state.tempInfo.maintenance?.crossReferenceSummary?.selectedTab || 0;
export const selectSelectedConnectionPair = (state: RootState) =>
  state.tempInfo.maintenance?.crossReferenceSummary?.selectedConnectionPair ||
  {};
export const selectConnectionPairDetails = (state: RootState) =>
  state.tempInfo.maintenance.connectionPairDetails;
export const selectAllCatalogCustomFields = (state: RootState) =>
  state.tempInfo.maintenance?.catalogSummary?.customFields ?? [];
export const selectCatalogCustomFieldsLoaded = (state: RootState) =>
  state.tempInfo.maintenance.catalogSummary.customFieldsLoaded;
export const selectCatalogDataFields = (state: RootState) => 
  state.tempInfo.maintenance.catalogDataFields.dataFields;
export const selectCatalogDataFieldsLoaded = (state: RootState) => 
  state.tempInfo.maintenance.catalogDataFields.dataFieldsLoaded;
// custom fields
export const getSelectedCustomField = (state: RootState) =>
  state.tempInfo.maintenance?.customFields?.selectedConnectionCustomField;
export const getSelectedConnectionId = (state: RootState) =>
  state.tempInfo.maintenance?.customFields?.selectedConnectionId;
export const getAllCustomFields = (state: RootState) =>
  state.tempInfo.maintenance?.customFields?.allCustomFields;
export const getAllConnections = (state: RootState) =>
  state.tempInfo.maintenance?.customFields?.allConnections;
export const getAllCustomFieldsAndValues = (state: RootState) =>
  state.tempInfo.maintenance?.customFields?.allCustomFieldsAndValues;
export const getCatalogDataFields = (state: RootState) => 
  state.tempInfo.maintenance.catalogDataFields.dataFields;

export const {
  setCatalogSummaryFilterTypes,
  setCrossReferenceSummaryFilterTypes,
  setSelectedConnectionPair,
  setCatalogSummarySelectedTab,
  setCatalogSummarySearchText,
  setCrossReferenceSummarySelectedTab,
  deleteCrossReferenceSummaryRecords,
  clearAllRecordCaches,
  clearTabCache,
  setSelectedCustomField,
  setSelectedConnectionId,
  setAllCustomFields,
  setAllConnections,
  setAllCustomFieldsAndValues,
  setCatalogCustomFields,
} = maintenanceSlice.actions;

export default maintenanceSlice.reducer;
